import {
  CircleEmptyQuestionIcon,
  ColumnDefinition,
  Tooltip,
  Typography,
} from "@codat/orchard-ui";
import { GetCompaniesResponse, GetCompanyResponse } from "api/public";
import classNames from "classnames";
import useInterval from "hooks/useInterval";
import { useUserState } from "portal-ui-remote/UserStateContext";
import React, { useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import { formatDateAndTimeWithShortString } from "utils/formatDate";
import formatUrl from "utils/formatUrl";

import { paths } from "components/App/paths";
import DataTable from "components/DataTable";
import { EditCompanyFormState } from "pages/Companies/EditCompanyModal";

import tableStyles from "../CompaniesTable/CompaniesTable.module.scss";
import { CompanyCell, CompanyCellModel } from "../TableCells/CompanyCell";
import {
  ConnectionsCell,
  ConnectionsCellModel,
} from "../TableCells/ConnectionsCell";
import { CreatedCell, CreatedCellModel } from "../TableCells/CreatedCell";
import { CTACellModel, CtaCell } from "../TableCells/CTACell";
import { LastPulledCell } from "../TableCells/LastPulledCell";
import styles from "./CreatedCompaniesTable.module.scss";

type CreatedCompanyTableRow = [
  CompanyCellModel,
  CreatedCellModel,
  ConnectionsCellModel,
  ConnectionsCellModel,
  ConnectionsCellModel,
  ConnectionsCellModel,
  CTACellModel
];

const HeaderLabel = () => {
  const [isOpen, setIsOpen] = useState(false);
  return (
    <div className={styles.headerLabel}>
      <Typography variant="body_sm" as="span">
        You just created...
      </Typography>
      <Tooltip
        isOpen={isOpen}
        placement={["top", "bottom"]}
        content={
          <div className={styles.tooltip}>
            <Typography as="p" variant="body_sm">
              {`These are companies you've just created in the Portal. They'll appear like any other company when you reload the page.`}
            </Typography>
          </div>
        }
      >
        <span
          onMouseEnter={() => setIsOpen(true)}
          onMouseLeave={() => setIsOpen(false)}
        >
          <CircleEmptyQuestionIcon />
        </span>
      </Tooltip>
    </div>
  );
};

export const MAX_COMPANIES_TO_SHOW = 5;
const POLL_INTERVAL = 10_000;

export function getColumnDefinitions(): ColumnDefinition<
  "name" | "created" | "lastSync" | "connections" | "cta"
>[] {
  return [
    {
      key: "name",
      label: <HeaderLabel />,
      component: CompanyCell,
      className: styles.companyCell,
      sortable: false,
      colWidth: 25,
    },
    {
      key: "created",
      label: "",
      component: CreatedCell,
      className: styles.genericCell,
      sortable: false,
    },
    {
      key: "lastSync",
      label: "",
      component: LastPulledCell,
      className: styles.genericCell,
      sortable: false,
    },
    {
      key: "connections",
      label: "",
      component: ConnectionsCell,
      className: styles.genericCell,
      colWidth: 30,
    },
    {
      key: "cta",
      label: "",
      component: CtaCell,
      className: styles.genericCell,
      align: "right",
    },
  ];
}

interface CreatedCompaniesTableProps {
  createdCompanies: GetCompaniesResponse | undefined;
  onRequestData: (company: GetCompanyResponse) => void;
  onDeleteButtonClick: (company: { id: string; name: string }) => void;
  onRowClick?: (index: number) => void;
  onEditButtonClick: (company: EditCompanyFormState) => void;
  loading?: boolean;
  onPollForUpdates: () => void;
}

export function CreatedCompaniesTable({
  createdCompanies,
  onRequestData,
  onDeleteButtonClick,
  onEditButtonClick,
  loading,
  onPollForUpdates,
}: CreatedCompaniesTableProps) {
  const history = useHistory();
  const { userState } = useUserState();
  const [buttonVariant, setButtonVariant] = useState<number | undefined>();
  useInterval(onPollForUpdates, POLL_INTERVAL);

  const hasReachedDisplayLimit = useMemo(
    () =>
      createdCompanies?.totalResults &&
      createdCompanies.totalResults > MAX_COMPANIES_TO_SHOW,
    [createdCompanies]
  );

  const userCanUpdate = useMemo(
    () =>
      userState?.userPermissions?.includes("clients_companies_edit") ?? false,
    [userState?.userPermissions]
  );

  const userCanViewCompanyData = useMemo(
    () =>
      [
        "data_all_view",
        "data_bankAccounts_view",
        "data_bankStatements_view",
        "data_bills_view",
        "data_billCreditNotes_view",
        "data_creditNotes_view",
        "data_customers_view",
        "data_financials_view",
        "data_info_view",
        "data_invoices_view",
        "data_journals_view",
        "data_payments_view",
        "data_suppliers_view",
      ].some((p) => userState?.userPermissions?.includes(p)),
    [userState?.userPermissions]
  );

  const userCanDeleteCompany = useMemo(
    () =>
      userState?.userPermissions?.includes("clients_companies_delete") ?? false,
    [userState?.userPermissions]
  );

  const tableRows = useMemo(() => {
    const tableRows = (
      createdCompanies?.results?.map((row, index) => {
        const onRowClick = userCanViewCompanyData
          ? () =>
              history.push(
                formatUrl(paths.companies.SUMMARY, { companyId: row.id })
              )
          : undefined;

        return {
          data: [
            {
              key: "name",
              value: {
                name: row.name,
                guid: row.id,
                userCanUpdate,
                onEditButtonClick: () =>
                  onEditButtonClick({
                    id: row.id,
                    name: row.name,
                    description: row.description,
                    tags: row.tags,
                    products: row.products,
                  }),
                searchQuery: undefined,
              },
            },
            {
              key: "created",
              value: {
                createdByUser: row.createdByUserName || "-",
                createdDate: row.created
                  ? formatDateAndTimeWithShortString(row.created, true)
                  : "-",
                searchQuery: undefined,
              },
            },
            {
              key: "lastSync",
              value: row.lastSync
                ? formatDateAndTimeWithShortString(row.lastSync, true)
                : "",
            },
            {
              key: "connections",
              value: {
                companyId: row.id,
                onPlatformClick: () => {
                  onRequestData(row);
                },
                maximumShownConnections: 2,
                connections: row.dataConnections,
              },
            },
            {
              key: "cta",
              value: {
                guid: row.id,
                numOfConnections: row.dataConnections.length,
                onRequestClick: () => onRequestData(row),
                onDeleteButtonClick: () => onDeleteButtonClick({ ...row }),
                onRowClick: onRowClick,
                showDelete: userCanDeleteCompany,
                buttonVariant: buttonVariant,
                index: index,
              },
            },
          ] as unknown as CreatedCompanyTableRow,
          onClick: onRowClick,
          onMouseEnter: onRowClick ? () => setButtonVariant(index) : undefined,
          onMouseLeave: onRowClick
            ? () => setButtonVariant(undefined)
            : undefined,
        };
      }) ?? []
    ).slice(0, MAX_COMPANIES_TO_SHOW);

    if (hasReachedDisplayLimit) {
      const leftoverCount =
        createdCompanies?.totalResults &&
        createdCompanies.totalResults - MAX_COMPANIES_TO_SHOW;
      tableRows.push({
        data: [
          {
            key: "name",
            value: {
              name: `...and ${leftoverCount} more`,
              guid: undefined,
              userCanUpdate: false,
              onEditButtonClick: undefined,
              hideSubtitle: true,
            },
          },
          {
            key: "created",
            value: {
              createdByUser: undefined,
              createdDate: undefined,
              hideCell: true,
            },
          },
          {
            key: "lastSync",
            value: undefined,
          },
          {
            key: "connections",
            value: {
              companyId: undefined,
              onPlatformClick: () => {
                // Do nothing
              },
              maximumShownConnections: 2,
              connections: [],
            },
          },
          {
            key: "cta",
            value: {
              guid: undefined,
              numOfConnections: 0,
              onRequestClick: () => null,
              onDeleteButtonClick: () => null,
              onRowClick: () => null,
              showDelete: false,
              buttonVariant: 0,
              index: undefined,
              hideCell: true,
            },
          },
        ] as unknown as CreatedCompanyTableRow,
        onClick: undefined,
        onMouseEnter: undefined,
        onMouseLeave: undefined,
      });
    }

    return tableRows;
  }, [
    createdCompanies,
    userCanUpdate,
    buttonVariant,
    history,
    onEditButtonClick,
    onRequestData,
    onDeleteButtonClick,
    userCanDeleteCompany,
    userCanViewCompanyData,
    hasReachedDisplayLimit,
  ]);

  return (
    <DataTable
      className={classNames(
        tableStyles.companiesTable,
        styles.createdCompaniesTable
      )}
      isSkeleton={loading}
      skeletonRows={MAX_COMPANIES_TO_SHOW}
      columnDefs={loading ? [] : getColumnDefinitions()}
      rowsInfo={tableRows}
      data-testid="created-companies-table"
      rowName="Company"
      buildEventProperties={(data) => {
        return { companyId: data[0].value.guid };
      }}
      isLayoutAuto
      hasBorder
    />
  );
}
